<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <Autocomplete
    class="Geocoding"
    density="compact"
    :placeholder="placeholder"
    :no-data-text="$t('geocoding.noData')"
    :items="geocodingItems"
    :clearable="true"
    :item-value="(val) => val"
    :item-title="(val) => val?.name ?? null"
    v-model="geoData"
    @update:search="(e) => (searchQuery = e)"
    @click:clear="reset"
    :error-messages="errorMessages"
  >
    <template #prependInner>
      <Icon :icon="icon" color="main-blue" class="opacity-100" />
    </template>
    <template #appendInner>
      <Transition name="fade">
        <ProgressCircular v-if="isLoading" color="#888" :width="2" :size="20" indeterminate />
      </Transition>
    </template>
  </Autocomplete>
</template>

<script setup lang="ts">
import { NominatimSearchType } from '@/models/enums/geo/NominatimSearchType'
import type { GeocodingItem } from '@/models/interfaces/geo/GeocodingItem'
import type { GeoData } from '@/models/interfaces/geo/GeoData'
import { i18n } from '@/plugins/i18n'
import GeocodingService from '@/services/map/GeocodingService'
import { debounce } from '@/services/utils/UtilsService'
import Autocomplete from '@/components/ui/Autocomplete.vue'
import Icon from '@/components/ui/Icon.vue'
import ProgressCircular from '@/components/ui/ProgressCircular.vue'
import type { Ref } from 'vue'
import { computed, ref, watch } from 'vue'

const geoData = defineModel<GeoData | null>({
  default: () => ({
    osmId: null,
    osmType: null,
    name: '',
    latitude: null,
    longitude: null,
  }),
})

const props = withDefaults(
  defineProps<{
    searchType: NominatimSearchType
    icon?: string
    geometryDetails?: boolean
    placeholder?: string
    errorMessages?: string
  }>(),
  {
    searchType: NominatimSearchType.FREE,
    icon: '$mapMarkerOutline',
    geometryDetails: false,
    placeholder: i18n.t('geocoding.placeholder'),
  },
)

const reset = () => {
  geoData.value = {
    osmId: null,
    osmType: null,
    name: '',
    latitude: null,
    longitude: null,
  }
  searchQuery.value = ''
}

watch(
  () => geoData.value,
  async () => {
    if (geoData.value && props.geometryDetails) {
      geoData.value = await GeocodingService.getBbox(geoData.value)
    }
  },
)

const searchQuery = ref('')
const features: Ref<GeocodingItem[]> = ref([])
const isLoading = ref(false)

const geocodingItems = computed(() => {
  return features.value.length > 0 ? features.value : []
})

const update = debounce(async () => {
  features.value = await GeocodingService.forwardGeocode(searchQuery.value, props.searchType)
  isLoading.value = false
}, 250)

watch(
  () => searchQuery.value,
  async () => {
    if (searchQuery.value) {
      isLoading.value = true
      update()
    }
  },
)
</script>
